import {
	ReactElement,
	useEffect,
	useState
} from "react";
import classNames from "classnames";
import { localize } from "client_legacy/ndaw/culture";
import { FormattedText, Icon } from "client/Components";
import { AvailableNotificationViewModel } from "client/Api/Notifications/notifications";
import { FrontActivity } from "./FrontActivity";
import styles from "./AnimatedStack.module.less";
import spacesStyles from "../ActivityFeedSpaces.module.less";
import { ExpandedStackHeaderActivityFeedItem } from "./ExpandedStackHeaderActivityFeedItem";
import { ExpandedStackFooterActivityFeedItem } from "./ExpandedStackFooterActivityFeedItem";
import { BehindActivity } from "./BehindActivity";
import { OrderedReadonlyArray } from "../store";
import { Activity } from "../Activity/Activity";
import {
	CollapsibleStack,
	CollapsibleStackStateKind
} from "./CollapsibleStack/CollapsibleStack";
import { setTrackingId } from "client/Misc/utils";

export type AnimatedStackFooterKind =
	| "none"
	| "only-padding"
	| "full";

export type AnimatedStackProps = {
	state: CollapsibleStackStateKind;
	title: string;
	activities: OrderedReadonlyArray<AvailableNotificationViewModel>;
	footerKind: AnimatedStackFooterKind;
	hasReducedPaddingAfterExpansion: boolean;
	totalNumberOfActivities: number;
	canBeDeletedByUser: boolean;
	stackId: string;
	onDelete(): void;
	onDeleteActivity(activity: AvailableNotificationViewModel): void;
	onMute(): void;
	onMuteActivity(activity: AvailableNotificationViewModel): void;
	onCollapse(): void;
	onCollapseFromFooter(): void;
	onExpansionStarted(): void;
	onExpanded(): void;
	onActivityChanged(): void;
	onActivityInvoked?: () => void;
};

export const AnimatedStack = (props: AnimatedStackProps): ReactElement | null => {
	const {
		state,
		title,
		activities,
		footerKind,
		hasReducedPaddingAfterExpansion,
		totalNumberOfActivities,
		canBeDeletedByUser,
		stackId,
		onDelete,
		onDeleteActivity,
		onMute,
		onMuteActivity,
		onCollapse,
		onCollapseFromFooter,
		onExpansionStarted,
		onExpanded,
		onActivityChanged,
		onActivityInvoked
	} = props;

	const [interactiveState, setInteractiveState] = useState<CollapsibleStackStateKind | undefined>();
	const calculatedState: CollapsibleStackStateKind = interactiveState || state;

	useEffect(
		() => {
			setInteractiveState(interactiveState => {
				return interactiveState === state
					? undefined
					: interactiveState;
			});
		},
		[state]
	);

	if (activities.length === 0) {
		return null;
	}

	if (activities.length === 1) {
		return (
			<Activity
				categoryName={title}
				activity={activities[0]}
				onDelete={() => onDeleteActivity(activities[0])}
				onMute={() => onMuteActivity(activities[0])}
				onActivityChanged={onActivityChanged}
				onActivityInvoked={onActivityInvoked}
			/>
		);
	}

	const areAllItemsDismissed: boolean = activities.every(a => a.isDismissed);

	const [firstActivity, secondActivity, thirdActivity, ...restOfActivities] = activities;

	const animationCompleteHandler = (): void => {
		if (calculatedState === "expanded") {
			onExpanded();
		}
	};

	const showMoreHandler = (): void => {
		setInteractiveState("expanded");
		onExpansionStarted();
	};

	const renderShowMoreItems = (): ReactElement => {
		return (
			<>
				<FormattedText
					text={localize("_NMore")}
					thingsToReplace={[
						{ what: "{{N}}", filler: totalNumberOfActivities - 1 }
					]}
				/>
				<span className={styles.arrowDown}>
					<Icon value="chevron-down" />
				</span>
			</>
		);
	};

	const renderFrontActivity = (): ReactElement => {
		return calculatedState === "collapsed"
			? (
				<FrontActivity
					categoryName={title}
					activity={firstActivity}
					canBeDeletedByUser={canBeDeletedByUser}
					isDismissed={areAllItemsDismissed}
					showMoreItemsContent={renderShowMoreItems()}
					onShowMoreItems={showMoreHandler}
					onDelete={onDelete}
					onMute={onMute}
					onActivityChanged={onActivityChanged}
				/>
			) : (
				<Activity
					categoryName={title}
					activity={firstActivity}
					onDelete={() => onDeleteActivity(firstActivity)}
					onMute={() => onMuteActivity(firstActivity)}
					onActivityChanged={onActivityChanged}
					onActivityInvoked={onActivityInvoked}
				/>
			);
	};

	const renderBehindActivity = (activity: AvailableNotificationViewModel): ReactElement => {
		return calculatedState === "collapsed"
			? (
				<BehindActivity
					categoryName={title}
					activity={activity}
					onActivityChanged={onActivityChanged}
				/>
			)
			: (
				<Activity
					categoryName={title}
					activity={activity}
					onDelete={() => onDeleteActivity(activity)}
					onMute={() => onMuteActivity(activity)}
					onActivityChanged={onActivityChanged}
					onActivityInvoked={onActivityInvoked}
				/>
			);
	};

	return (
		<div
			{...setTrackingId("animated-stack", `|${stackId}`)}
			className={classNames(
				{ [spacesStyles.collapsedStackItem_hasTwoActivities]: state === "collapsed" && activities.length === 2 },
				{ [spacesStyles.collapsedStackItem_hasThreeOrMoreActivities]: state === "collapsed" && activities.length >= 3 }
			)}
		>
			<CollapsibleStack
				state={calculatedState}
				onAnimationComplete={animationCompleteHandler}
				coverElementIndex={1}
				itemVisualBottomExpanse={15}
				itemVisualSideExpanse={15}
				coverElementVisualTopOffset={20}
			>
				<div className={classNames(
					styles.header,
					{ [styles.header_collapsed]: calculatedState === "collapsed" }
				)}>
					<ExpandedStackHeaderActivityFeedItem
						stackId={stackId}
						title={title}
						hasReducedPadding={hasReducedPaddingAfterExpansion}
						canStackBeDeletedByUser={canBeDeletedByUser}
						onDelete={onDelete}
						onCollapse={onCollapse}
						onMute={onMute}
					/>
				</div>
				<div>
					{renderFrontActivity()}
				</div>
				<div
					className={classNames(
						styles.secondActivity,
						{ [styles.secondActivity_collapsed]: calculatedState === "collapsed" }
					)}
				>
					{renderBehindActivity(secondActivity)}
				</div>
				{thirdActivity && (
					<div
						className={classNames(
							styles.thirdActivity,
							{ [styles.thirdActivity_collapsed]: calculatedState === "collapsed" }
						)}
					>
						{renderBehindActivity(thirdActivity)}
					</div>
				)}
				{restOfActivities.map(a => (
					<div
						className={classNames(
							styles.hiddenActivity,
							{ [styles.hiddenActivity_collapsed]: calculatedState === "collapsed" }
						)}
						key={a.id}
					>
						{renderBehindActivity(a)}
					</div>
				))}
				{footerKind !== "none" && (
					<ExpandedStackFooterActivityFeedItem
						stackId={stackId}
						shouldRenderButtons={footerKind === "full"}
						canStackBeDeletedByUser={canBeDeletedByUser}
						onDelete={onDelete}
						onCollapse={onCollapseFromFooter}
						onMute={onMute}
					/>
				)}
			</CollapsibleStack>
		</div>
	);
};

AnimatedStack.displayName = "AnimatedStack";